<script setup>
import { ref, computed } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { platformLabel as platformName } from '@/lib/platformLabel'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import PublicGallery from '@/Components/Public/PublicGallery.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import BookingRequestForm from '@/Components/Public/BookingRequestForm.vue'
import PaymentMethods from '@/Components/Public/PaymentMethods.vue'
import MobileBar from '@/Components/Public/MobileBar.vue'
import CheckoutCard from '@/Components/Public/CheckoutCard.vue'
import DateField from '@/Components/Public/DateField.vue'
import { datePart, phuketPickupAt, toPhuketWallClock } from '@/lib/phuketTime'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  transfer:  { type: Object,  required: true },
  faq:       { type: Object,  default: () => ({}) },
  base_path: { type: String,  default: '/transfers' },
  zones:     { type: Array,   default: () => [] },
  checkout_enabled: { type: Boolean, default: false }
})

// Request-to-book: its own pickup state, independent of CheckoutCard's
// richer local state (hotel, class, passengers travel in the free-text note).
const today = computed(() => new Date().toLocaleDateString('en-CA')) // YYYY-MM-DD, local
// A pickup needs the hour too — the office dispatches a driver — and the
// same 6h lead time the checkout card asks for.
const requestPickup = ref('')
const minPickup = computed(() => toPhuketWallClock(new Date(Date.now() + 6 * 60 * 60 * 1000)))

const photos = props.transfer.photos?.length ? props.transfer.photos : []

function formatDuration(mins) {
  if (!mins) return ''
  if (mins < 60) return t('public.transfers.duration_min', { n: mins })
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m > 0 ? t('public.transfers.duration_hm', { h, m }) : t('public.transfers.duration_h', { h })
}
const durationLabel = computed(() => formatDuration(props.transfer.duration_minutes))

const bookingCtas = computed(() =>
  Object.entries(props.transfer.contact_intents || {}).map(([platform, url]) => ({ platform, url, name: platformName(platform) }))
)
</script>

<template>
  <Head>
    <title>{{ transfer.route_label }} · {{ t('public.head_title') }}</title>
    <meta name="description" :content="transfer.description">
  </Head>

  <BrandHero kind="transfer" :eyebrow="durationLabel" :title="transfer.route_label">
    <template #top>
      <a :href="base_path" class="bhero-back">← {{ t('public.transfers.back') }}</a>
    </template>
    <p v-if="transfer.bidirectional" class="bhero-lede">{{ t('public.transfers.bidirectional_note') }}</p>
    <div v-if="transfer.contact_intents && Object.keys(transfer.contact_intents).length" class="bhero-ctas">
      <LuxButton
        v-for="(url, platform, idx) in transfer.contact_intents"
        :key="platform"
        :href="url"
        :variant="idx === 0 ? 'solid' : 'line'"
        tone="dark"
        :glyph="platform"
        target="_blank"
        rel="noopener"
      >{{ t('public.write_on', { platform: platformName(platform) }) }}</LuxButton>
    </div>
  </BrandHero>

  <section class="body">
    <div class="body__inner">
      <PublicGallery :photos="photos" :alt="transfer.route_label">
        <template #placeholder>
          <CatalogPlaceholder kind="transfer" />
        </template>
      </PublicGallery>

      <div class="detail">
        <div class="detail__content">
          <section v-if="transfer.description" class="card">
            <h2 class="section-title">{{ t('public.transfers.sections.about') }}</h2>
            <p class="prose">{{ transfer.description }}</p>
          </section>

          <section class="card">
            <h2 class="section-title">{{ t('public.transfers.sections.classes') }}</h2>
            <div class="classes">
              <table class="classes__table">
                <thead>
                  <tr>
                    <th></th>
                    <th>{{ t('public.transfers.fields.seats') }}</th>
                    <th>{{ t('public.transfers.fields.luggage') }}</th>
                    <th>{{ t('public.transfers.fields.price') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="o in transfer.class_options" :key="o.vehicle_class">
                    <th scope="row">{{ t(`public.transfers.vehicle_classes.${o.vehicle_class}`) }}</th>
                    <td>{{ o.seats }}</td>
                    <td>{{ o.luggage }}</td>
                    <td class="classes__price fw-value">{{ transfer.price_on_request ? t('public.vip.price_on_request') : o.display_price }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section v-if="faq.items && faq.items.length" class="card">
            <h2 class="section-title">{{ faq.heading }}</h2>
            <details v-for="(f, i) in faq.items" :key="i" class="faq">
              <summary class="faq__q">{{ f.q }}</summary>
              <p class="faq__a prose">{{ f.a }}</p>
            </details>
          </section>
        </div>

        <aside class="detail__aside">
          <div class="facts">
            <h2 class="section-title">{{ t('public.transfers.fields.duration') }}</h2>
            <p class="facts__duration fw-value">{{ durationLabel }}</p>
          </div>

          <div class="trust-card">
            <h2 class="section-title">{{ t('public.transfers.trust.title') }}</h2>
            <ul class="trust-list">
              <li>{{ t('public.transfers.trust.no_payment') }}</li>
              <li>{{ t('public.transfers.trust.real_team') }}</li>
              <li>{{ t('public.transfers.trust.local_support') }}</li>
            </ul>
          </div>

          <div id="request-to-book" class="plan-card">
            <h2 class="section-title">{{ t('public.transfers.booking.title') }}</h2>
            <!-- price_on_request has no fixed price to ever pay online for —
                 "instant online payment is coming soon" is misleading there
                 (it reads as a temporary gap, not a bespoke-pricing route).
                 Only show the coming-soon note when checkout is merely not
                 configured YET (M3, fix-wave). -->
            <p v-if="!checkout_enabled && !transfer.price_on_request" class="plan-note">{{ t('public.transfers.booking.note') }}</p>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.checkout.transfer.pickup_label') }}</span>
              <DateField v-model="requestPickup" type="datetime-local" :min="minPickup" class="plan-date" :placeholder="t('public.pick_datetime')" />
            </label>
            <h3 class="plan-subtitle">{{ t('public.booking_request.title') }}</h3>
            <BookingRequestForm
              bookable-type="transfer"
              :bookable-id="transfer.id"
              :details="{ start_on: datePart(requestPickup), pickup_at: phuketPickupAt(requestPickup) }"
              :disabled="!requestPickup"
            />
            <p v-if="bookingCtas.length" class="plan-divider">{{ t('public.booking_request.or_chat') }}</p>
            <div v-if="bookingCtas.length" class="plan-ctas">
              <LuxButton v-for="c in bookingCtas" :key="c.platform" :href="c.url" variant="line" tone="light" :glyph="c.platform" block target="_blank" rel="noopener">{{ t('public.transfers.booking.cta_via', { platform: c.name }) }}</LuxButton>
            </div>
            <PaymentMethods inline />
          </div>

          <CheckoutCard
            v-if="checkout_enabled && !transfer.price_on_request"
            bookable-type="transfer"
            :bookable-id="transfer.id"
            :from-point="transfer.from_point"
            :to-point="transfer.to_point"
            :from-point-label="transfer.from_point_label"
            :to-point-label="transfer.to_point_label"
            :bidirectional="transfer.bidirectional"
            :class-options="transfer.class_options"
            :zones="zones"
          />
        </aside>
      </div>
    </div>
  </section>
  <MobileBar target="request-to-book" :value="transfer.price_on_request ? t('public.vip.price_on_request') : (transfer.from_price ? `${t('public.transfers.from')} ${transfer.from_price}` : '')" :cta="t('public.transfers.booking.title')" />
</template>

<style scoped>
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-back:hover { opacity: 1; }
.bhero-lede { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 52ch; opacity: 0.94; }
.bhero-vip {
  display: inline-flex; align-items: center; margin: 1rem 0.4rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: var(--ink); color: var(--warn); border: 1px solid var(--warn);
}
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }

.body { background: var(--bg); }
.body__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; }
.detail { display: grid; grid-template-columns: 1fr; gap: 1.5rem; margin-top: 1.5rem; }
@media (min-width: 880px) { .detail { grid-template-columns: 1.6fr 0.9fr; gap: 2rem; align-items: start; } }
.detail__content { display: grid; gap: 1rem; }
@media (min-width: 880px) { .detail__aside { position: sticky; top: 1.5rem; } }
.facts { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.facts__duration { font-size: 1.3rem; margin: 0; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.section-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }

.classes__table { width: 100%; border-collapse: collapse; }
.classes__table th, .classes__table td { padding: 0.6rem 0.75rem; text-align: left; border-bottom: 1px solid var(--line); }
.classes__table thead th { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--muted); font-weight: 600; }
.classes__table tbody th { color: var(--ink); font-weight: 700; }
.classes__price { color: var(--warn); font-size: 1.05rem; }
.classes__table tbody tr:last-child th, .classes__table tbody tr:last-child td { border-bottom: none; }

.faq { border-top: 1px solid var(--line); padding: 0.75rem 0; }
.faq:first-of-type { border-top: none; }
.faq__q { cursor: pointer; font-weight: 600; color: var(--ink); }
.faq__a { margin-top: 0.5rem; }

.trust-card {
  margin-top: 1.25rem; padding: 1.25rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-table, 0.75rem);
}
.trust-list { list-style: none; margin: 0.75rem 0 0; padding: 0; display: grid; gap: 0.6rem; }
.trust-list li { position: relative; padding-left: 1.6rem; color: var(--inkSoft); line-height: 1.4; }
.trust-list li::before { content: "✓"; position: absolute; left: 0; top: 0; color: var(--ok); font-weight: 700; }

.plan-card {
  margin-top: 1.25rem; padding: 1.25rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-table, 0.75rem);
}
.plan-note { margin: 0 0 0.85rem; color: var(--inkSoft); font-size: 0.9rem; line-height: 1.5; }
.plan-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 0.85rem; }
.plan-label { color: var(--inkSoft); font-size: 0.95rem; }
.plan-date {
  border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem);
  background: var(--bg); color: var(--ink); padding: 0.4rem 0.6rem; font: inherit;
  color-scheme: light;
}
.plan-subtitle { margin: 1.1rem 0 0; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); }
.plan-divider { margin: 0.9rem 0 0; text-align: center; font-size: 0.8rem; color: var(--muted); }
.plan-ctas { margin-top: 1rem; display: grid; gap: 0.5rem; }
</style>
